import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router';
import ExperimentDashboard from 'components/main/ExperimentDashboard';
import AddProtein from 'components/main/ProteinFormulation/AddProtein';
import Cookies from 'js-cookie';
import Layout from './components/main/Layout';
import Login from './components/main/Login/Login';
import Logout from 'components/main/Login/Logout';
import './App.scss';
import TreeControl from './components/main/TreeControl';
import ProjectDashboard from 'components/main/ProjectDashboard';
import { setNavigateFunction } from 'utils/Global';
import LicenseViolate from 'components/main/Licensing/LicenseViolation';
import RegisterComputer from 'components/main/Licensing/RegisterComputer';
import SessionTermination from 'components/main/Licensing/SessionTermination';
import UpdatedDashboard from 'components/UpdatedDashboard/Dashboard';
import RMMessage from 'components/main/RMMessage';
import HybridSelection from 'components/main/Licensing/HybridSelection';
import SearchComponent from 'components/UpdatedDashboard/SearchComponent';
import SeedDashboard from 'components/main/SeedDashboard';
import AccessControlError from 'components/main/Error/AccessControlError';
import VersionMismatchError from 'components/main/Error/VersionMismatchError';
import { SetupTabs } from 'components/main/Setup/GeneralSetup/SetupTabs';
import { crystal as Xtal, synchrotron as Ston } from './pages';
import SynchrotronTrip from 'components/synchrotron';
import { SettingsTabs } from 'components/main/Settings/SettingsTabs';
import { ModalContext } from 'components/modal/modalContext';
import {
  BackdropLoadingContainer,
  Modal,
  ModalProps,
  defaultAlertModalState,
} from 'components/modal';
import { Fragment } from 'components/main/Fragment/Fragment';
import SearchAndFilter from 'pages/searchAndFilter';
import FeatureSelection from 'components/main/Licensing/FeatureSelection';
import ForgotPassword from 'components/main/userGroup/user/forgotPassword';
import InputOTP from 'components/main/userGroup/user/inputOTP';
import useRMLite from 'CustomHooks/useRMLite';
import { RightClickProvider } from 'components/rightClick/rightClickProvider';
import RightClickWrapper from 'components/rightClick/rightClickWrapper';
import GuardedRoute from 'components/GuardedRoute';
import { ROUTE_PATHS } from 'components/main/constants';

const rmliteRestrictedRoutes = [
  { path: '/seed/:id', component: <SeedDashboard /> },
  { path: `${ROUTE_PATHS.PROTEIN}/:nodeId`, component: <AddProtein /> },
  { path: `${ROUTE_PATHS.SCREEN}/:id`, component: <ExperimentDashboard /> },
  { path: `${ROUTE_PATHS.SCREEN}/:id/info`, component: <ExperimentDashboard /> },
  {
    path: `${ROUTE_PATHS.SCREEN}/:id/plate/:plateID`,
    component: <ExperimentDashboard />,
  },
  { path: '/synchrotrontrip/:id', component: <SynchrotronTrip /> },
  { path: '/setup/:name/*', component: <SetupTabs /> },
  { path: '/synchrotron', component: <Ston /> },
  { path: '/crystal', component: <Xtal /> },
  { path: '/settings/:tab?', component: <SettingsTabs /> },
  { path: `${ROUTE_PATHS.SEARCH_AND_FILTER}/:id/*`, component: <SearchAndFilter /> },
  { path: `${ROUTE_PATHS.FRAGMENT}/:id`, component: <Fragment /> },
];

const rmLiteRestrictionMessage =
  'The feature you are trying to access is not available in Rock Maker Lite.';
import WellDropImageStore, {
  DEFAULT_WELL_DROP_IMAGE_CACHE_MAX_SIZE_MB,
} from 'components/well/wellDropImageStore';
import FocusLevelImageStore from 'components/well/focusLevelImageStore';
import store from 'redux/configureStore';
import { systemPropertiesCache } from 'generic/systemPropertiesCache';
import { PropertyConstants } from 'enums/PropertyConstants';
import { IntPropertyDTO } from 'business/models/Models';
import withErrorBoundary from 'components/main/Error/withErrorBoundary';
import { useFetchLicenseValidatorConfig } from 'business/licenseValidator/hooks';
import SeatLicenseViolation from 'components/main/Licensing/SeatLicenseViolation';

const App = () => {
  const [modalDetails, setModalDetails] = useState<ModalProps>(defaultAlertModalState);
  const isRMLite = useRMLite();
  useFetchLicenseValidatorConfig();
  const navigate = useNavigate();

  // Track open tabs so the last closed tab can clear stale openedNodesIds
  // used by multi-window node opening (openNode / dashboards).
  const onUnload = () => {
    const cookiesTabs =
      Cookies.get('openedTabs') === undefined ? 0 : Cookies.get('openedTabs');
    const openedTabs = parseInt(cookiesTabs as string) - 1;
    if (openedTabs > 0) {
      Cookies.set('openedTabs', String(openedTabs));
    } else if (openedTabs === 0) {
      localStorage.removeItem('openedNodesIds');
      Cookies.set('openedTabs', String(openedTabs));
    }
  };

  const onLoad = () => {
    const cookiesTabs =
      Cookies.get('openedTabs') === undefined ? 0 : Cookies.get('openedTabs');
    const openedTabs = parseInt(cookiesTabs as string) + 1;
    Cookies.set('openedTabs', String(openedTabs));
  };

  // This function can be used to initialize any global state or perform setup tasks
  const handleInitialization = async () => {
    WellDropImageStore.injectDispatch(store.dispatch);
    FocusLevelImageStore.injectDispatch(store.dispatch);
    const prop = await systemPropertiesCache.get(
      PropertyConstants.DropImagingCacheMaxSizeMB,
    );
    const maxSizeMB =
      Number((prop as IntPropertyDTO)?.value) ||
      DEFAULT_WELL_DROP_IMAGE_CACHE_MAX_SIZE_MB;
    WellDropImageStore.configureMaxTotalSizeMB(maxSizeMB);
  };

  useEffect(() => {
    // Register this tab immediately; 'load' may already have fired by mount time.
    onLoad();
    window.addEventListener('beforeunload', onUnload);
    handleInitialization();
    setNavigateFunction(navigate);

    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  return (
    <ModalContext.Provider value={{ modalDetails, setModalDetails }}>
      <BackdropLoadingContainer>
        <Layout>
          <RightClickProvider>
            <RightClickWrapper>
              <Routes>
                <Route path={ROUTE_PATHS.ROOT} element={<UpdatedDashboard />} />
                <Route path="/search" element={<SearchComponent />} />
                <Route
                  path={`${ROUTE_PATHS.PROJECT}/:treeNodeId?`}
                  element={<ProjectDashboard />}
                />
                <Route path={ROUTE_PATHS.LOGIN} element={<Login />} />
                <Route path={ROUTE_PATHS.LOGOUT} element={<Logout />} />
                <Route path={ROUTE_PATHS.INPUT_OTP} element={<InputOTP />} />
                <Route path={ROUTE_PATHS.FORGOT_PASSWORD} element={<ForgotPassword />} />
                <Route
                  path={`${ROUTE_PATHS.EXPERIMENT}/:id`}
                  element={<ExperimentDashboard />}
                />
                <Route
                  path={`${ROUTE_PATHS.EXPERIMENT}/:id/plate/:plateID`}
                  element={<ExperimentDashboard />}
                />
                <Route
                  path={`${ROUTE_PATHS.EXPERIMENT}/:id/:plateID/:wellNum/:dropNumber/:cpID/:regionName/:imageBatchId/:wellMode`}
                  element={<ExperimentDashboard />}
                />
                <Route
                  path={`${ROUTE_PATHS.EXPERIMENT}/:id/info`}
                  element={<ExperimentDashboard />}
                />
                <Route path="/explore" element={<TreeControl />} />
                <Route path={ROUTE_PATHS.LICENSE_VIOLATE} element={<LicenseViolate />} />
                <Route path={ROUTE_PATHS.RM_MESSAGE} element={<RMMessage />} />
                <Route
                  path={ROUTE_PATHS.REGISTER_COMPUTER}
                  element={<RegisterComputer />}
                />
                <Route
                  path={ROUTE_PATHS.SESSION_TERMINATION}
                  element={<SessionTermination />}
                />
                <Route
                  path={ROUTE_PATHS.HYBRID_SELECTION}
                  element={<HybridSelection />}
                />
                <Route
                  path={ROUTE_PATHS.FEATURE_SELECTION}
                  element={<FeatureSelection />}
                />
                <Route
                  path={ROUTE_PATHS.ACCESS_CONTROL_ERROR}
                  element={<AccessControlError />}
                />
                <Route
                  path={ROUTE_PATHS.VERSION_MISMATCH}
                  element={<VersionMismatchError />}
                />
                <Route
                  path={ROUTE_PATHS.SEAT_LICENSE_VIOLATE}
                  element={<SeatLicenseViolation />}
                />
                {rmliteRestrictedRoutes.map(({ path, component }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <GuardedRoute
                          path={path}
                          component={component}
                          isAllowed={!isRMLite}
                          message={rmLiteRestrictionMessage}
                        />
                      }
                    />
                  );
                })}
                {/* Fallback route */}
                <Route path="*" element={<Navigate to={ROUTE_PATHS.ROOT} replace />} />
              </Routes>
            </RightClickWrapper>
          </RightClickProvider>
        </Layout>
      </BackdropLoadingContainer>
      <Modal {...modalDetails} />
    </ModalContext.Provider>
  );
};

export default withErrorBoundary(App);
